import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as StripePayoutsIcon } from '../../../assets/icons/sidebar/stripe-payout.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import {
  RequestStripePayoutsInterface,
  StripePayoutsInterface,
} from '../../../interface/stripe-payouts.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableApprovedStripePayoutsApplied,
  fetchApprovedStripePayoutsMethodFilters,
  fetchStripePayouts,
  setSortData,
  updateApprovedStripePayoutsFilters,
  refreshApprovedStripePayouts,
} from '../../../redux/slices/ApprovedStripePayoutsSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
// import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
// import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
// import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import { capitalizeFirstLetter } from '../../../shared-functions/StringFunctions';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
// import stripePayoutsApi from '../../../utils/api/stripe-payouts';

/**
 * Approved stripe payouts components
 * @returns {any} The result
 */
function ApprovedEMSPPayouts() {
  const { t, i18n } = useTranslation('stripePayout');
  const {
    payouts,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.stripePayouts);
  const dispatch = useAppDispatch();
  // const [exportLoader, setExportLoader] = useState(false);
  // const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  /**
   * Handle the fetch the data in api function
   * @param {any} data - The data
   */
  const fetchData = (data: RequestStripePayoutsInterface) => {
    if (!loading) {
      dispatch(fetchStripePayouts(data));
    }
  };

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageNateLimitChange = (value: RequestStripePayoutsInterface) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  const column = [
    {
      label: t('accountID'),
      accessorKey: 'accountCustomId' as keyof StripePayoutsInterface,
      sortAllow: false,
    },
    {
      label: t('accountName'),
      accessorKey: 'accountName' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('email'),
      accessorKey: 'email' as keyof StripePayoutsInterface,
      sortAllow: false,
    },
    {
      label: t('revenueFromDate'),
      accessorKey: 'fromDate' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('revenueToDate'),
      accessorKey: 'toDate' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('payoutID'),
      accessorKey: 'transferId' as keyof StripePayoutsInterface,
      sortAllow: false,
    },
    {
      label: t('payoutAmount'),
      accessorKey: 'amount' as keyof StripePayoutsInterface,
      sortAllow: true,
      /**
       * Payment amount view in table
       * @param {any} payoutsStatus - The payoutsStatus
       * @returns {any} The result
       */
      customData: (payoutsStatus: StripePayoutsInterface) => (
        <div>{ToFixedValue(payoutsStatus.amount)}</div>
      ),
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof StripePayoutsInterface,
      sortAllow: false,
      /**
       * Payment status view in table
       * @param {any} payoutsStatus - The payoutsStatus
       * @returns {any} The result
       */
      customData: (payoutsStatus: StripePayoutsInterface) => (
        <div className="status-in-stripe-payout-table">
          <p className={`${payoutsStatus.status === 'transferred' ? 'success-text' : null}`}>
            {capitalizeFirstLetter(payoutsStatus.status)}
          </p>
        </div>
      ),
    },
    {
      label: t('payoutDate'),
      accessorKey: 'transferredAt' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
  ];

  /**
   * Current columns for export
   */
  // const currentColumns = [
  //   { label: t('sNo'), accessorKey: '__serial__' },
  //   { label: t('accountID'), accessorKey: 'accountCustomId' },
  //   { label: t('accountName'), accessorKey: 'accountName' },
  //   { label: t('email'), accessorKey: 'email' },
  //   { label: t('revenueFromDate'), accessorKey: 'fromDate' },
  //   { label: t('revenueToDate'), accessorKey: 'toDate' },
  //   { label: t('payoutID'), accessorKey: 'transferId' },
  //   { label: t('payoutAmount'), accessorKey: 'amount' },
  //   { label: t('status'), accessorKey: 'status' },
  //   { label: t('payoutDate'), accessorKey: 'transferredAt' },
  // ];

  /**
   * Filtering columns for export without hidden columns
   */
  // const exportCurrentColumns = currentColumns.filter(
  //   (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  // );

  /**
   * Export data pdf or csv function
   * @param {string} type - The type of export
   */
  // const exportDataPdfOrCSV = (type: string) => {
  //   setExportLoader(true);
  //   const queryParameters = GetParametersFromCommonFilters(filters);
  //   stripePayoutsApi
  //     .exportStripePayouts({
  //       ...queryParameters,
  //       submissionStatus: 'transferred',
  //       sortBy,
  //       sortField,
  //     })
  //     .then((response) => {
  //       const headers = exportCurrentColumns.map((col) => col.label);
  //       const rows = response.data.map((row: StripePayoutsInterface, index: number) =>
  //         exportCurrentColumns.map((col) => {
  //           switch (col.accessorKey) {
  //             case '__serial__':
  //               return index + 1;

  //             case 'fromDate':
  //               return row.fromDate ? ConvertUTCtoDate(row.fromDate) : t('na');

  //             case 'toDate':
  //               return row.toDate ? ConvertUTCtoDate(row.toDate) : t('na');

  //             case 'transferredAt':
  //               return row.transferredAt ? ConvertUTCtoDate(row.transferredAt) : t('na');

  //             case 'amount':
  //               return row.amount ? ToFixedValue(row.amount) : '0.00';

  //             case 'status':
  //               return row.status ? capitalizeFirstLetter(row.status) : t('na');

  //             default:
  //               return (row as any)[col.accessorKey] || t('na');
  //           }
  //         }),
  //       );
  //       setExportLoader(false);
  //       if (rows.length > 0) {
  //         if (type === 'csv') {
  //           exportCSV(headers, rows, 'approved-payouts-list.csv');
  //         } else {
  //           exportPDF(headers, rows, 'approved-payouts-list.pdf');
  //         }
  //       }
  //     })
  //     .catch(() => {
  //       setExportLoader(false);
  //     });
  // };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  /**
   * Function to refresh data
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshApprovedStripePayouts({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  useEffect(() => {
    if (isFilterUpdated || payouts.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableApprovedStripePayoutsApplied());
    }
  }, [filters]);
  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('emspPayouts'),
        icon: <StripePayoutsIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults, t]);

  return (
    <div className="table-container" dir={i18n.dir()}>
      <CustomTable
        header={column}
        data={payouts}
        version="v2"
        serialNo
        limit={limit}
        page={page}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        sizeChangeButtonRequired
        filters={filters}
        filtersToDispatch={updateApprovedStripePayoutsFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchApprovedStripePayoutsMethodFilters}
        requiredRefreshToggle
        refreshFunction={handleAutoRefresh}
        refreshRequired
        refershCurrentPages={handleRefresh}
        handleRefresh={handleRefresh}
        height={320}
        hoverLabel={t('refresh')}
        // exportLoader={exportLoader}
        // exportDataPdfOrCSV={permissionCheck('mobilityPayoutsExport') ? exportDataPdfOrCSV : undefined}
        // onHiddenColumnsChange={setHiddenColumns}
      />
    </div>
  );
}

export default ApprovedEMSPPayouts;
